import { Box, Button, Stack, styled, Typography } from "@mui/material";
import AceEditor from "react-ace";
import "ace-builds/src-noconflict/mode-javascript";
import "ace-builds/src-noconflict/mode-html";
import "ace-builds/src-noconflict/mode-css";
import "ace-builds/src-noconflict/theme-monokai";
import "ace-builds/src-noconflict/ext-beautify"; // Import the beautify extension
import "ace-builds/src-noconflict/ext-searchbox";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";

const EditorContainer = styled(Box)(({ theme }) => ({
  background: "#1d1e22",
  color: "white",
  height: "100%",
}));

interface ICodeEditorProps {
  title: string;
  mode: string;
  height?: number;
  value: string;
  onChange: (value: string) => void;
  actionButton?: {
    label: string;
    onClick: () => void;
  };
}

export interface CodeEditorHandle {
  beautifyCode: () => void;
}

const CodeEditor = forwardRef<CodeEditorHandle, ICodeEditorProps>(
  ({ title, mode, height, value, onChange, actionButton }, ref) => {
    const [containerRef, setContainerRef] = useState<HTMLDivElement | null>(
      null
    );
    const onRefChange = useCallback((node) => {
      setContainerRef(node);
    }, []);
    const editorHeight = useMemo(() => {
      if (!containerRef) {
        return undefined;
      }
      if (!height) {
        return `calc(100% - ${containerRef.clientHeight + 4}px)`;
      }
      return `${Math.floor(height - containerRef.clientHeight + 4)}px`;
    }, [height, containerRef]);

    const beautifyCode = useCallback(() => {
      const ace = (window as any).ace;
      if (ace && ace.require) {
        const beautify = ace.require("ace/ext/beautify");
        const editor = ace.edit(title);

        // terrible hack to ensure the code is beautified
        beautify.beautify(editor.session);
        beautify.beautify(editor.session);
        beautify.beautify(editor.session);
        beautify.beautify(editor.session);
        beautify.beautify(editor.session);
      }
    }, [title]);

    
    useImperativeHandle(ref, () => ({
      beautifyCode,
    }));

    useEffect(() => {
      beautifyCode();
    }, [beautifyCode]);

    return (
      <EditorContainer>
        <Stack
          direction={"row"}
          justifyContent="space-between"
          alignItems={"center"}
          p={1}
          ref={onRefChange}
        >
          <Typography variant="subtitle2" color={"inherit"}>
            {title}
          </Typography>
          <Stack direction="row" spacing={1}>
            {actionButton && (
              <Button size="extraSmall" onClick={actionButton.onClick}>
                {actionButton.label}
              </Button>
            )}
            <Button size="extraSmall" onClick={beautifyCode}>
              Beautify
            </Button>
          </Stack>
        </Stack>

        <AceEditor
          mode={mode}
          theme="monokai"
          name={title}
          fontSize={12}
          width={"100%"}
          tabSize={4}
          height={editorHeight}
          setOptions={{
            useWorker: false,
            enableBasicAutocompletion: true,
            enableLiveAutocompletion: true,
            enableSnippets: true,
          }}
          wrapEnabled
          showPrintMargin={false}
          value={value}
          onChange={(v) => onChange(v)}
        />
      </EditorContainer>
    );
  }
);

export default CodeEditor;
